import { getDailyGameKey } from './storageKeys';

const DAILY_ID_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export function formatDailyId(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function parseDailyId(dailyId: string): Date | null {
  if (!DAILY_ID_REGEX.test(dailyId)) return null;
  const date = new Date(`${dailyId}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  if (formatDailyId(date) !== dailyId) return null;
  return date;
}

export function getSystemDailyId(): string {
  return formatDailyId(new Date());
}

const getDailyIdOverride = (): string | null => {
  if (typeof window === 'undefined') return null;
  const hash = window.location?.hash ?? '';
  const queryStart = hash.indexOf('?');
  if (queryStart === -1) return null;
  const params = new URLSearchParams(hash.slice(queryStart + 1));
  const override = params.get('date');
  if (!override) return null;
  const trimmed = override.trim();
  return parseDailyId(trimmed) ? trimmed : null;
};

export function getActiveDate(): Date {
  const override = getDailyIdOverride();
  if (override) {
    const parsed = parseDailyId(override);
    if (parsed) return parsed;
  }
  return new Date();
}

export function formatDailyIdLabel(
  dailyId: string,
  options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
): string {
  const parsed = parseDailyId(dailyId);
  if (!parsed) return dailyId;
  return parsed.toLocaleDateString(undefined, options);
}

export type GameId = 'yak-pong' | 'wordle' | 'queens' | 'word-scramble' | 'number-match' | 'jigsaw';

export type GameStatus = 'played' | 'available';

export interface DailyGameState {
  lastPlayedDate: string; // YYYY-MM-DD
  completed: boolean;
  score?: number; // Time in seconds, moves, etc.
  metadata?: any; // Extra data like "won/lost", guesses, etc.
}

// Get the current date in YYYY-MM-DD format (local time)
export function getDailyId(): string {
  return getDailyIdOverride() ?? getSystemDailyId();
}

// Get a daily seed based on the date
export function getDailySeed(): number {
  const dailyId = getDailyId();
  return parseInt(dailyId.replace(/-/g, ''), 10);
}

// Seeded random number generator (0-1)
export function seededRandom(seed: number): number {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

// Game Epoch - January 6, 2026
const GAME_EPOCH = new Date('2026-01-06T00:00:00');

export function getDailyGameIndexForDailyId(dailyId: string): number {
  const currentDate = parseDailyId(dailyId) ?? new Date(`${dailyId}T00:00:00`);
  const epochDate = new Date(GAME_EPOCH.toISOString().split('T')[0] + 'T00:00:00');

  const diffTime = currentDate.getTime() - epochDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
}

export function getDailyGameIndex(): number {
  return getDailyGameIndexForDailyId(getDailyId());
}

// Get game state from local storage
export function getGameState(gameId: GameId, dateId: string = getDailyId()): DailyGameState | null {
  try {
    const key = getDailyGameKey(gameId, dateId);
    const stored = localStorage.getItem(key);
    if (stored) return JSON.parse(stored) as DailyGameState;

    const legacyKey = getDailyGameKey(gameId);
    const legacyStored = localStorage.getItem(legacyKey);
    if (!legacyStored) return null;
    const legacyState = JSON.parse(legacyStored) as DailyGameState;
    if (legacyState.lastPlayedDate !== dateId) return null;
    localStorage.setItem(key, JSON.stringify(legacyState));
    return legacyState;
  } catch (e) {
    console.error('Failed to load game state', e);
    return null;
  }
}

// Save game state to local storage
export function saveGameState(gameId: GameId, state: DailyGameState, dateId: string = getDailyId()) {
  try {
    const key = getDailyGameKey(gameId, dateId);
    const normalizedState = { ...state, lastPlayedDate: dateId };
    localStorage.setItem(key, JSON.stringify(normalizedState));
    localStorage.setItem(getDailyGameKey(gameId), JSON.stringify(normalizedState));
    // Trigger a storage event manually or custom event for UI updates if needed
    window.dispatchEvent(new Event('daily-games-updated'));
  } catch (e) {
    console.error('Failed to save game state', e);
  }
}

export const ALL_GAME_IDS: GameId[] = ['yak-pong', 'wordle', 'queens', 'word-scramble', 'number-match', 'jigsaw'];

// Order of "next game" from game completion
const DAILY_GAME_ORDER: GameId[] = ['wordle', 'number-match', 'word-scramble', 'jigsaw', 'queens'];

export const GAME_NAMES: Record<GameId, string> = {
  'yak-pong': 'YakPong',
  wordle: 'Yakle',
  'word-scramble': 'Scramble',
  queens: 'Queens',
  'number-match': 'Memory',
  jigsaw: 'Jigsaw',
};

export const GAME_ROUTES: Record<GameId, string> = {
  'yak-pong': '#/yak-pong',
  wordle: '#/yakle',
  'number-match': '#/number-match',
  'word-scramble': '#/word-scramble',
  queens: '#/queens',
  jigsaw: '#/jigsaw',
};

export function getGameRoute(gameId: GameId): string {
  return GAME_ROUTES[gameId];
}

export function getNextUnplayedGame(): string {
  const info = getNextUnplayedGameInfo();
  return info ? info.route : '#/';
}

export function getNextUnplayedGameInfo(): { id: GameId; name: string; route: string } | null {
  for (const gameId of DAILY_GAME_ORDER) {
    if (!isGamePlayedToday(gameId)) {
      return {
        id: gameId,
        name: GAME_NAMES[gameId],
        route: getGameRoute(gameId)
      };
    }
  }
  return null;
}

export function isGamePlayedToday(gameId: GameId): boolean {
  const state = getGameState(gameId);
  const today = getDailyId();
  return !!state && state.lastPlayedDate === today && state.completed;
}

export function getFormattedDate(): string {
    const date = getActiveDate();
    return date.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
}

export function formatPuzzleId(date: Date): string {
  return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function getDailyIdRange(startId: string, endId: string): string[] {
  const startDate = parseDailyId(startId);
  const endDate = parseDailyId(endId);
  if (!startDate || !endDate) return [];
  if (startDate > endDate) return [];

  const dates: string[] = [];
  const cursor = new Date(startDate);
  while (cursor <= endDate) {
    dates.push(formatDailyId(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

export function resetGameState(gameId?: GameId) {
  const removeGameKeys = (id: GameId) => {
    const prefix = getDailyGameKey(id);
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    }
    console.log(`Reset game state for: ${id}`);
  };

  if (gameId) {
    removeGameKeys(gameId);
  } else {
    ALL_GAME_IDS.forEach(removeGameKeys);
    console.log('Reset all game states');
  }
  window.dispatchEvent(new Event('daily-games-updated'));
  window.location.reload();
}

// Expose the reset helper in browser builds without breaking worker runtimes.
if (typeof window !== 'undefined') {
  (window as typeof window & { resetGame?: typeof resetGameState }).resetGame = resetGameState;
}
